/**
 * @fileoverview ComparisonRules
 * @description Chooses what counts as "the same" once two seats are paired.
 *
 *              Pairing already decides whether two records are the same
 *              person. These rules decide everything after that: does the
 *              deadhead flag still agree, does the trip, does the position.
 *              They are editable because the answer is not universal — a crew
 *              ID differing across two systems with separate ID spaces is
 *              expected, while a deadhead flag differing is a defect, and only
 *              someone who knows the two systems can say which is which.
 *
 *              A rule names a field on each side, so a checkpoint that
 *              publishes a value under a different normalized field can still
 *              be checked against its neighbour. Rules whose fields are not
 *              mapped on both sides of a hop are skipped at run time rather
 *              than reported as differences.
 */

import React from 'react';
import {
  Box, Button, Chip, IconButton, MenuItem, Paper, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import { ArrowRight, Plus, RotateCcw, Scale, Trash2 } from 'lucide-react';

const TEAL = '#0097A9';
const NAVY = '#01426A';

const selectSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#fff', fontSize: '0.78rem' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: TEAL, borderWidth: 2,
  },
};

const SEVERITIES = [
  { value: 'mismatch', label: 'Fails the hop', color: '#b45309', bg: '#fffbeb' },
  { value: 'info', label: 'Report only', color: '#0891b2', bg: '#ecfeff' },
];

function RuleRow({ rule, index, crewFields, modes, onChange, onRemove }) {
  const severity = SEVERITIES.find((s) => s.value === rule.severity) || SEVERITIES[0];
  const mode = modes.find((m) => m.mode === rule.mode);
  const off = !rule.enabled;

  const set = (patch) => onChange(index, { ...rule, ...patch });

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.25, borderRadius: '10px', border: '1px solid #e5e7eb',
        borderLeft: `3px solid ${off ? '#cbd5e1' : severity.color}`,
        opacity: off ? 0.55 : 1, transition: 'opacity 160ms ease',
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
        <Tooltip title={off ? 'Check this field' : 'Skip this field'}>
          <Switch
            size="small" checked={Boolean(rule.enabled)}
            onChange={() => set({ enabled: !rule.enabled })}
            inputProps={{ 'aria-label': `Check ${rule.left_field}` }}
            sx={{
              '& .Mui-checked': { color: TEAL },
              '& .Mui-checked + .MuiSwitch-track': { bgcolor: TEAL },
            }}
          />
        </Tooltip>

        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flex: 1.6, minWidth: 0 }}>
          <TextField
            select size="small" sx={{ ...selectSx, flex: 1 }}
            label="Upstream field"
            value={rule.left_field}
            onChange={(e) => set({ left_field: e.target.value })}
          >
            {crewFields.map((f) => (
              <MenuItem key={f.key} value={f.key} sx={{ fontSize: '0.8rem' }}>{f.label}</MenuItem>
            ))}
          </TextField>
          <ArrowRight size={14} color="#94a3b8" style={{ flexShrink: 0 }} />
          <TextField
            select size="small" sx={{ ...selectSx, flex: 1 }}
            label="Downstream field"
            value={rule.right_field}
            onChange={(e) => set({ right_field: e.target.value })}
          >
            {crewFields.map((f) => (
              <MenuItem key={f.key} value={f.key} sx={{ fontSize: '0.8rem' }}>{f.label}</MenuItem>
            ))}
          </TextField>
        </Stack>

        <Tooltip title={mode?.description || ''}>
          <TextField
            select size="small" sx={{ ...selectSx, flex: 0.8, minWidth: 140 }}
            label="Compare as"
            value={rule.mode}
            onChange={(e) => set({ mode: e.target.value })}
          >
            {modes.map((m) => (
              <MenuItem key={m.mode} value={m.mode} sx={{ fontSize: '0.8rem' }}>{m.label}</MenuItem>
            ))}
          </TextField>
        </Tooltip>

        <TextField
          select size="small" sx={{ ...selectSx, flex: 0.8, minWidth: 150 }}
          label="When it differs"
          value={rule.severity}
          onChange={(e) => set({ severity: e.target.value })}
        >
          {SEVERITIES.map((s) => (
            <MenuItem key={s.value} value={s.value} sx={{ fontSize: '0.8rem' }}>{s.label}</MenuItem>
          ))}
        </TextField>

        <Tooltip title="Remove rule">
          <IconButton size="small" onClick={() => onRemove(index)} sx={{ color: '#dc2626' }}>
            <Trash2 size={15} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

const ComparisonRules = ({ rules, crewFields, modes, defaultRules, onChange }) => {
  const list = rules || [];

  const update = (index, next) => onChange(list.map((r, i) => (i === index ? next : r)));
  const remove = (index) => onChange(list.filter((_, i) => i !== index));
  const add = () =>
    onChange([
      ...list,
      {
        left_field: crewFields[0]?.key || 'crew_id',
        right_field: crewFields[0]?.key || 'crew_id',
        mode: 'exact',
        severity: 'mismatch',
        enabled: true,
      },
    ]);

  const active = list.filter((r) => r.enabled);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} mb={1.5}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Scale size={16} color={NAVY} />
          <Box>
            <Typography variant="subtitle2" fontWeight={800} color={NAVY}>
              What counts as a match
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Applied to every paired seat. A rule is skipped on any hop where either side does not map its fields.
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} flexShrink={0}>
          <Button
            size="small" variant="outlined" startIcon={<RotateCcw size={13} />}
            onClick={() => onChange(defaultRules.map((r) => ({ ...r })))}
            sx={{
              borderColor: '#cbd5e1', color: '#475569', borderRadius: '8px',
              textTransform: 'none', fontWeight: 600, fontSize: '0.72rem',
            }}
          >
            Defaults
          </Button>
          <Button
            size="small" variant="outlined" startIcon={<Plus size={13} />} onClick={add}
            sx={{
              borderColor: TEAL, color: TEAL, borderRadius: '8px',
              textTransform: 'none', fontWeight: 700, fontSize: '0.72rem',
            }}
          >
            Add rule
          </Button>
        </Stack>
      </Stack>

      <Stack
        direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap
        sx={{ mb: 1.5, p: 1.25, borderRadius: '10px', bgcolor: '#f8fafc', border: '1px solid #e5e7eb' }}
      >
        {active.length ? (
          active.map((rule, index) => {
            const severity = SEVERITIES.find((s) => s.value === rule.severity) || SEVERITIES[0];
            const label = crewFields.find((f) => f.key === rule.left_field)?.label || rule.left_field;
            const right = crewFields.find((f) => f.key === rule.right_field)?.label || rule.right_field;
            return (
              <Chip
                key={`${rule.left_field}-${rule.right_field}-${index}`}
                size="small"
                label={rule.left_field === rule.right_field ? label : `${label} → ${right}`}
                sx={{
                  fontWeight: 700, fontSize: '0.66rem',
                  bgcolor: severity.bg, color: severity.color,
                  border: `1px solid ${severity.color}22`,
                }}
              />
            );
          })
        ) : (
          <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 600 }}>
            No rules are enabled — hops will only report crew that are missing entirely.
          </Typography>
        )}
      </Stack>

      <Stack spacing={1}>
        {list.map((rule, index) => (
          <RuleRow
            key={index}
            rule={rule}
            index={index}
            crewFields={crewFields}
            modes={modes}
            onChange={update}
            onRemove={remove}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default ComparisonRules;
